import { Injectable, inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable } from "rxjs";
import { tap } from "rxjs/operators";
import { IndexedDBService } from "./indexeddb.service";
import { environment } from "../../environments/environment";

export interface UserRoles {
  site_admin: boolean;
}

@Injectable({
  providedIn: "root",
})
export class RolesService {
  private http = inject(HttpClient);
  private indexedDB = inject(IndexedDBService);
  private rolesSubject = new BehaviorSubject<UserRoles>({ site_admin: false });
  private permissionsSubject = new BehaviorSubject<string[]>([]);

  public roles$ = this.rolesSubject.asObservable();
  public permissions$ = this.permissionsSubject.asObservable();

  async fetchUserRoles(): Promise<void> {
    // First, check IndexedDB for cached userType
    try {
      const cachedUserType = await this.indexedDB.getItem(
        "settings",
        "userType",
      );

      if (cachedUserType) {
        console.log(
          `✅ Loaded userType from IndexedDB (cache hit): ${cachedUserType}`,
        );
        const is_site_admin = cachedUserType === "admin";
        this.rolesSubject.next({ site_admin: is_site_admin });

        // Set permissions based on cached userType
        const permissions = this.getPermissionsByUserType(cachedUserType);
        this.permissionsSubject.next(permissions);
        return;
      }
    } catch (error) {
      console.warn("Failed to check IndexedDB for userType:", error);
    }

    // If not in IndexedDB, fetch from backend
    console.log("📥 Fetching userType from API (cache miss)");
    this.http
      .get<{ roles: UserRoles; permissions: string[] }>("/api/roles", {
        params: { rolesKey: environment.rolesKey },
      })
      .subscribe({
        next: async (response) => {
          console.log("✅ Received roles from API:", response);
          this.rolesSubject.next(response.roles);
          this.permissionsSubject.next(response.permissions || []);

          // Save userType to IndexedDB
          const userType = response.roles.site_admin ? "admin" : "guest";
          await this.indexedDB.setItem("settings", "userType", userType);
          console.log(`💾 Saved userType: ${userType} to IndexedDB`);
        },
        error: (error) => {
          console.error("❌ Failed to fetch user roles:", error);
          console.error("Error details:", {
            status: error.status,
            statusText: error.statusText,
            message: error.message,
          });
        },
      });
  }

  private getPermissionsByUserType(userType: string): string[] {
    if (userType === "admin") {
      return ["read", "write", "delete"];
    } else if (userType === "editor") {
      return ["read", "write"];
    } else {
      return ["read"];
    }
  }

  isSiteAdmin(): boolean {
    return this.rolesSubject.value.site_admin;
  }

  getRoles(): UserRoles {
    return this.rolesSubject.value;
  }

  getPermissions(): string[] {
    return this.permissionsSubject.value;
  }

  hasPermission(permission: string): boolean {
    return this.permissionsSubject.value.includes(permission);
  }

  async clearRoles(): Promise<void> {
    this.rolesSubject.next({ site_admin: false });
    this.permissionsSubject.next([]);

    // Remove userType from IndexedDB
    await this.indexedDB.removeItem("settings", "userType");
    console.log("🗑️ Removed userType from IndexedDB");
  }
}
